/**
 * AssetDrawerContent Component
 *
 * Displays the content section for each asset drawer tab.
 * Pure presentation component - all state management handled by parent.
 *
 * Uses local state pattern (via useAssetDrawerTab) to prevent tab alternation
 * bug caused by React Router setSearchParams race conditions during rapid
 * arrow key navigation.
 */

import { ClockIcon } from '@heroicons/react/24/outline';
import React, { useMemo } from 'react';

import { Loader } from '@/components/Loader';
import { Container } from '@/components/ui';
import { AssetAffiliation } from '@/sections/asset/components/tabs';
import { TechnologyAttributeUnion } from '@/sections/asset/components/tabs';
import {
  AttributesTabContent,
  isCloudResource,
} from '@/sections/asset/components/tabs';
import { NotesTab, VulnerabilitiesTab } from '@/sections/asset/components/tabs';
import { Asset, Attribute, RiskCombinedStatusCount } from '@/types';
import { Regex } from '@/utils/regex.util';

// AssetOverview is passed as a prop to avoid circular dependencies

export interface AssetDrawerContentProps {
  asset: Asset;
  assetKey: string;
  assetWithRisk: Asset & {
    riskSummary: Record<string, number>;
  };
  riskCounts: RiskCombinedStatusCount;
  isInitialLoading: boolean;
  attributes: Record<string, Attribute[]>;
  technologies: TechnologyAttributeUnion[];
  entityIdentifier: string;
  entityGroup: string;
  isAssetGroupADomain: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  AssetOverview: React.ComponentType<any>;
  /** Handler to open the drawer's comment modal */
  onOpenCommentModal: () => void;
  /** Current tab value from local state */
  currentTab: string;
  /** Tab change handler from local state hook */
  onTabChange: (tab: string) => void;
}

/**
 * AssetDrawerContent routes to the appropriate tab content based on selected tab:
 * - Overview: Main asset information with relationships and vulnerabilities
 * - Vulnerabilities: Detailed vulnerability list
 * - Attributes: Asset attributes and technologies
 * - Notes: Asset notes and comments
 * - History: Asset affiliation and change history
 *
 * This is a pure presentation component with no internal state.
 */
export function AssetDrawerContent({
  asset,
  assetKey,
  assetWithRisk,
  riskCounts,
  isInitialLoading,
  attributes,
  technologies,
  entityIdentifier,
  entityGroup,
  isAssetGroupADomain,
  AssetOverview,
  onOpenCommentModal,
  currentTab,
  onTabChange,
}: AssetDrawerContentProps) {
  const isAssetIdentifierAnIpAddress =
    Regex.IPV4.test(entityIdentifier || '') ||
    Regex.IPV6.test(entityIdentifier || '');

  const ptr = attributes.ptr || [];
  const registrant = attributes.registrant || [];

  const isAssetNotFound = useMemo(
    () => Object.keys(asset).length === 0 && !isInitialLoading,
    [asset, isInitialLoading]
  );

  return (
    <div className="min-h-0 w-full flex-1 overflow-y-auto">
      <Container
        className="flex h-full flex-col p-0"
        tabIndex={-1}
        id={currentTab}
      >
        <Loader
          isLoading={isInitialLoading}
          className="m-6 w-[calc(100%-48px)]"
        >
          {currentTab === '' &&
            (isAssetNotFound ? (
              <div className="flex items-center gap-4 p-6">
                <ClockIcon className="text-chariot-stroke size-6" />
                <span className="text-chariot-text-secondary">
                  Asset is no longer detected in your attack surface
                </span>
              </div>
            ) : (
              <AssetOverview
                attributesByName={{
                  ptr,
                  country: [{ value: asset.country }],
                  asname: [{ value: asset.asname }],
                  asnumber: [{ value: asset.asnumber }],
                  asrange: [{ value: asset.asrange }],
                  ipaddress: isAssetIdentifierAnIpAddress
                    ? [{ value: entityIdentifier || '' }]
                    : [],
                  arn: [],
                  name: [],
                  resourceType: [],
                  region: [],
                  account: [],
                  subscription: [],
                  project: [],
                  ...(isCloudResource(asset)
                    ? {
                        arn: [{ value: asset.properties?.Arn }],
                        name: [{ value: asset.name }],
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        resourceType: [{ value: (asset as any).resourceType }],
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        region: [{ value: (asset as any).region }],
                      }
                    : {}),
                  ...(isCloudResource(asset) &&
                  asset.key?.startsWith('#awsresource#')
                    ? {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        account: [{ value: (asset as any).accountRef }],
                      }
                    : {}),
                  ...(isCloudResource(asset) &&
                  asset.key?.startsWith('#azureresource#')
                    ? {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        subscription: [{ value: (asset as any).accountRef }],
                      }
                    : {}),
                  ...(isCloudResource(asset) &&
                  asset.key?.startsWith('#gcpresource#')
                    ? {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        project: [{ value: (asset as any).accountRef }],
                      }
                    : {}),
                }}
                asset={assetWithRisk}
                domain={
                  isAssetGroupADomain && entityGroup
                    ? {
                        name: entityGroup,
                        registrantOrg: registrant?.[0]?.value || '',
                      }
                    : undefined
                }
                riskCounts={riskCounts as RiskCombinedStatusCount}
                isAssetIdentifierAnIpAddress={isAssetIdentifierAnIpAddress}
                isAssetIdentifierACloudResource={isCloudResource(asset)}
                onTabChange={onTabChange}
              />
            ))}
          {currentTab === 'vulnerabilities' && (
            <VulnerabilitiesTab assetKey={assetKey || ''} />
          )}
          {currentTab === 'attributes' && (
            <AttributesTabContent asset={asset} technologies={technologies} />
          )}
          {currentTab === 'notes' && (
            <NotesTab asset={asset} onOpenCommentModal={onOpenCommentModal} />
          )}
          {currentTab === 'history' && <AssetAffiliation asset={asset} />}
        </Loader>
      </Container>
    </div>
  );
}
